// frontend/admin-portal/src/components/treasury/TransactionModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Alert } from "@fiberhome/shared-ui";
import { treasuryService } from "../../services/treasuryService";

export default function TransactionModal({
  open,
  accounts = [],
  categories = [],
  onClose,
  onSaved,
}) {
  const [mode, setMode] = useState("out"); // 'in' | 'out' | 'transfer'
  const [form, setForm] = useState({
    accountId: "",
    fromAccountId: "",
    toAccountId: "",
    categoryId: "",
    amount: "",
    description: "",
    occurredAt: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setError("");
    setMode("out");
    const def = accounts.find((a) => a.isDefault) || accounts[0];
    setForm({
      accountId: def?.id || "",
      fromAccountId: "",
      toAccountId: "",
      categoryId: "",
      amount: "",
      description: "",
      occurredAt: new Date().toISOString().slice(0, 10),
    });
  }, [open, accounts]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset the opposite-mode selections when switching tabs so a stale
  // accountId doesn't leak into a transfer (or vice versa).
  const changeMode = (next) => {
    setMode(next);
    setError("");
    if (next === "transfer") {
      setForm((f) => ({
        ...f,
        accountId: "",
        categoryId: "",
        fromAccountId: f.fromAccountId || f.accountId || "",
        toAccountId: "",
      }));
    } else {
      setForm((f) => ({
        ...f,
        fromAccountId: "",
        toAccountId: "",
        accountId: f.accountId || f.fromAccountId || "",
      }));
    }
  };

  // Client-side validation — return an error string or null
  const validate = () => {
    const amt = Number(form.amount);
    if (!form.amount || !Number.isFinite(amt) || amt <= 0) {
      return "Enter a positive amount";
    }
    if (mode === "transfer") {
      if (!form.fromAccountId) return "Select the source account";
      if (!form.toAccountId) return "Select the destination account";
      if (String(form.fromAccountId) === String(form.toAccountId)) {
        return "Source and destination must be different";
      }
    } else {
      if (!form.accountId) return "Select an account";
    }
    return null;
  };

  const canSubmit = () => {
    const amt = Number(form.amount);
    if (!form.amount || !Number.isFinite(amt) || amt <= 0) return false;
    if (mode === "transfer") {
      if (!form.fromAccountId || !form.toAccountId) return false;
      if (String(form.fromAccountId) === String(form.toAccountId)) return false;
    } else {
      if (!form.accountId) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        type: mode,
        amount: Number(form.amount),
        description: form.description?.trim() || null,
        // Send undefined if empty — the server falls back to now.
        // Sending "" would produce an Invalid Date on the backend.
        occurredAt: form.occurredAt || undefined,
      };

      if (mode === "transfer") {
        payload.fromAccountId = Number(form.fromAccountId);
        payload.toAccountId = Number(form.toAccountId);
      } else {
        payload.accountId = Number(form.accountId);
        payload.categoryId = form.categoryId ? Number(form.categoryId) : null;
      }

      await treasuryService.createTransaction(payload);
      onSaved?.();
      onClose?.();
    } catch (err) {
      // Prefer the server's message, then the response body's error/detail,
      // then the axios-level message, then a generic fallback.
      const serverMsg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        "Failed to save transaction";
      setError(serverMsg);
    } finally {
      setSaving(false);
    }
  };

  const filteredCategories = categories.filter((c) => {
    if (mode === "in") {
      return c.group === "income" || c.group === "owner" || c.group === "other";
    }
    // out
    return (
      c.group === "expense" ||
      c.group === "investment" ||
      c.group === "owner" ||
      c.group === "other"
    );
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Transaction"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            disabled={!canSubmit()}
          >
            Save
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* Mode tabs */}
      <div className="d-flex gap-2 mb-3">
        <Button
          size="sm"
          variant={mode === "in" ? "success" : "ghost"}
          onClick={() => changeMode("in")}
        >
          Money In
        </Button>
        <Button
          size="sm"
          variant={mode === "out" ? "danger" : "ghost"}
          onClick={() => changeMode("out")}
        >
          Money Out
        </Button>
        <Button
          size="sm"
          variant={mode === "transfer" ? "primary" : "ghost"}
          onClick={() => changeMode("transfer")}
        >
          Transfer
        </Button>
      </div>

      {/* From/To for transfer, Account/Category otherwise */}
      {mode === "transfer" ? (
        <div className="row g-3">
          <div className="col-md-6">
            <label className="fh-input-label">From *</label>
            <select
              className="fh-input"
              value={form.fromAccountId}
              onChange={(e) => set("fromAccountId", e.target.value)}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.currency})
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="fh-input-label">To *</label>
            <select
              className="fh-input"
              value={form.toAccountId}
              onChange={(e) => set("toAccountId", e.target.value)}
            >
              <option value="">Select account</option>
              {accounts
                .filter((a) => String(a.id) !== String(form.fromAccountId))
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.currency})
                  </option>
                ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-md-6">
            <label className="fh-input-label">Account *</label>
            <select
              className="fh-input"
              value={form.accountId}
              onChange={(e) => set("accountId", e.target.value)}
            >
              <option value="">Select account</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.currency})
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="fh-input-label">Category</label>
            <select
              className="fh-input"
              value={form.categoryId}
              onChange={(e) => set("categoryId", e.target.value)}
            >
              <option value="">—</option>
              {filteredCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Amount / Date / Description */}
      <div className="row g-3 mt-2">
        <div className="col-md-4">
          <Input
            label="Amount *"
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <Input
            label="Date"
            type="date"
            value={form.occurredAt}
            onChange={(e) => set("occurredAt", e.target.value)}
          />
        </div>
        <div className="col-md-12 mt-2">
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
